<template>
  <div class="app-view">
    <!-- Loading mask: spans the upload call and the async extraction job, so
         the citizen never sees a half-loaded step 2 mid-transition. -->
    <div v-if="showLoadingMask" class="loading-mask">
      <div class="loading-mask__content">
        <span class="spinner spinner--lg"></span>
        <p class="loading-mask__title">Ihre Dokumente werden von FormIQ 3 ausgewertet…</p>
        <p class="loading-mask__hint">Das kann einige Minuten dauern. Bitte haben Sie einen Moment Geduld.</p>
      </div>
    </div>

    <!-- Top bar -->
    <header class="app-view__header">
      <button class="back-btn" @click="goBack">← Dashboard</button>
      <div class="header__title">
        <span class="header__icon">🌳</span>
        <div>
          <span class="header__name">Baumschutz – Ausnahmegenehmigung</span>
          <span class="header__sub">Antrag auf Ausnahmegenehmigung nach Baumschutzverordnung</span>
        </div>
      </div>
      <div class="header__badge">FormIQ 3 · Dokument-Check</div>
    </header>

    <ShowcaseBanner />

    <!-- Main layout: content + sidebar -->
    <div class="app-view__body">

      <!-- Left: step content -->
      <div class="form-area">

        <!-- Step 0: Upload documents / create case -->
        <template v-if="currentStep === 0">
          <p class="form-area__intro">
            Laden Sie Ihre Unterlagen hoch und ordnen Sie jedem Dokument die passende Kategorie zu.
            Es wird für jede der unten aufgeführten Kategorien mindestens ein Dokument benötigt.
          </p>
          <p v-if="loadError" class="notice notice--error">⚠ {{ loadError }}</p>
          <CategorizedUploadDropzone
            :categories="categories"
            :busy="isCreating"
            :error-message="createError"
            button-label="Hochladen & kategorisieren"
            @submit="createCase"
          />
        </template>

        <!-- Step 1: Review categories -->
        <template v-else-if="currentStep === 1">
          <div class="bookmark-notice">
            🔖 Merken Sie sich diese Seite, um später den Status zu prüfen, weitere Unterlagen
            hinzuzufügen oder die Prüfung durch den Sachbearbeiter einzusehen.
          </div>

          <p v-if="isExtractionFailed" class="notice notice--error">
            ⚠ Die automatische Auswertung Ihrer Dokumente ist fehlgeschlagen. Bitte prüfen Sie die
            hochgeladenen Dateien (z. B. auf Beschädigung oder leere Seiten) und laden Sie sie erneut
            hoch, oder wenden Sie sich an die Sachbearbeitung.
          </p>

          <div v-if="documentsDirty && !isProcessing" class="reextract-banner">
            <p>
              Sie haben Dokumente hinzugefügt oder entfernt. Starten Sie die Neuauswertung, damit
              FormIQ 3 Ihre Antragsdetails auf den aktuellen Dokumentenstand aktualisiert.
            </p>
            <button class="nav-btn nav-btn--primary" :disabled="isReextracting" @click="reextract">
              {{ isReextracting ? 'Wird gestartet…' : '🔄 Neu auswerten' }}
            </button>
          </div>
          <p v-if="reextractError" class="notice notice--error">⚠ {{ reextractError }}</p>

          <p v-if="loadError" class="notice notice--error">⚠ {{ loadError }}</p>

          <section class="documents-section">
            <div class="documents-section__header">
              <h3 class="section-title">Hochgeladene Dokumente</h3>
              <button type="button" class="edit-toggle-btn" @click="isEditMode = !isEditMode">
                {{ isEditMode ? '✓ Fertig' : '✎ Bearbeiten' }}
              </button>
            </div>
            <p v-if="!documents.length" class="empty-hint">Noch keine Dokumente hochgeladen.</p>
            <div v-for="group in groupedDocuments" :key="group.id" class="category-group">
              <div class="category-group__header">
                <span class="category-group__label">{{ group.label }}</span>
                <span class="category-group__count">{{ group.docs.length }}</span>
              </div>
              <CaseDocumentCard
                v-for="doc in group.docs"
                :key="doc.document_id"
                :document="doc"
                :categories="categories"
                :client-id="resolvedClientId"
                :case-id="caseId"
                :editable="isEditMode"
                @deleted="onDocumentDeleted"
              />
            </div>
          </section>

          <section v-if="isEditMode" class="add-more-section">
            <h3 class="section-title">Weitere Dokumente hinzufügen</h3>
            <CategorizedUploadDropzone
              :categories="categories"
              :require-all-categories="false"
              :busy="isAdding"
              :error-message="addError"
              button-label="Hochladen"
              @submit="onAddFiles"
            />
          </section>
        </template>

        <!-- Steps 2..N: one step per detected document category (FormIQ 3 AutoFill) -->
        <template v-else-if="currentStep >= antragsdetailsStart && currentStep < angabenStepIndex">
          <p v-if="isExtractionFailed" class="notice notice--error">
            ⚠ Die automatische Verarbeitung ist fehlgeschlagen, es liegen keine erkannten Angaben vor.
          </p>
          <p v-else-if="!extractions.length" class="empty-hint">
            Es liegen noch keine erkannten Angaben vor. Dieser Schritt füllt sich automatisch,
            sobald FormIQ 3 die hochgeladenen Dokumente ausgewertet hat.
          </p>
          <BaumAntragsdetailsStep
            v-else
            :model-value="reviewableExtractions[currentStep - antragsdetailsStart].item"
            :show-errors="showErrors"
            @update:model-value="updateExtractionItem(reviewableExtractions[currentStep - antragsdetailsStart].originalIndex, $event)"
          />
        </template>

        <!-- Personal details (manual entry, not extracted) -->
        <template v-else-if="currentStep === angabenStepIndex">
          <BaumAngabenStep v-model="formData" :show-errors="showErrors" />
        </template>

        <!-- Legal notice / agreement -->
        <template v-else-if="currentStep === rechtlicheStepIndex">
          <BaumRechtlicheHinweiseStep v-model="formData" :show-errors="showErrors" />
        </template>

        <!-- Summary & submit -->
        <template v-else>
          <h3 class="section-title">Zusammenfassung</h3>

          <div class="info-box info-box--green">
            <span class="info-box__icon">✅</span>
            <div>
              <strong>Antrag vollständig ausgefüllt</strong>
              <p>Bitte prüfen Sie Ihre Angaben und Dokumente vor dem Absenden.</p>
            </div>
          </div>

          <section class="summary-section">
            <div class="summary-section__title">Hochgeladene Dokumente</div>
            <p class="empty-hint" v-if="!documents.length">Keine Dokumente vorhanden.</p>
            <template v-for="group in groupedDocuments" :key="group.id">
              <div v-for="doc in group.docs" :key="doc.document_type" class="summary-row">
                <span class="summary-row__label">{{ group.label }}</span>
                <span class="summary-row__value">📄 {{ doc.document_name }}</span>
              </div>
            </template>
          </section>

          <section v-for="category in summaryCategories" :key="category.title" class="summary-section">
            <div class="summary-section__title">{{ category.title }}</div>
            <p class="empty-hint" v-if="!category.fields.length">Keine Angaben erfasst.</p>
            <div v-for="field in category.fields" :key="field.label" class="summary-row">
              <span class="summary-row__label">{{ field.label }}</span>
              <span class="summary-row__value" :class="{ autofilled: field.autofilled }">
                {{ field.value || '–' }}
                <span v-if="field.autofilled" class="badge badge--blue" style="margin-left:6px">✦ AutoFill</span>
              </span>
            </div>
          </section>

          <section class="summary-section">
            <div class="summary-section__title">Persönliche Angaben</div>
            <div v-for="field in angabenSummary" :key="field.label" class="summary-row">
              <span class="summary-row__label">{{ field.label }}</span>
              <span class="summary-row__value">{{ field.value || '–' }}</span>
            </div>
          </section>

          <section class="summary-section">
            <div class="summary-section__title">Rechtliche Hinweise</div>
            <div class="summary-row">
              <span class="summary-row__label">Zustimmung</span>
              <span class="summary-row__value">
                {{ formData.confirm_rechtliche_hinweise ? '✓ Erteilt' : '– Nicht erteilt' }}
              </span>
            </div>
          </section>

          <div class="completeness">
            <label class="completeness__checkbox">
              <input type="checkbox" v-model="completenessConfirmed" />
              Ich bestätige, dass ich alle erforderlichen Unterlagen vollständig hochgeladen habe.
            </label>
          </div>
        </template>
      </div>

      <!-- Right: Sidebar navigation -->
      <aside class="sidebar">
        <div class="sidebar__header">
          <span class="sidebar__icon">📋</span>
          <span class="sidebar__title">Antragsnavigation</span>
        </div>
        <nav class="sidebar__nav">
          <div
            v-for="(step, index) in steps"
            :key="index"
            class="sidebar__item"
            :class="{
              'sidebar__item--active': index === currentStep,
              'sidebar__item--done': index < currentStep,
              'sidebar__item--locked': index > currentStep,
            }"
            @click="goToStep(index)"
          >
            <div class="sidebar__indicator">
              <span v-if="index < currentStep" class="indicator--done">✓</span>
              <span v-else-if="index === currentStep" class="indicator--active">{{ index + 1 }}</span>
              <span v-else class="indicator--pending">{{ index + 1 }}</span>
            </div>
            <span class="sidebar__label">{{ step }}</span>
          </div>
        </nav>
        <div class="sidebar__progress">
          <div class="progress-bar">
            <div class="progress-bar__fill" :style="{ width: progressPercent + '%' }"></div>
          </div>
          <span class="progress-text">{{ currentStep + 1 }} von {{ steps.length }} Schritten</span>
        </div>
      </aside>
    </div>

    <!-- Navigation bar at bottom -->
    <div class="nav-bar">
      <button
        class="nav-btn nav-btn--secondary"
        :disabled="currentStep === 0"
        @click="prevStep"
      >
        ← Zurück
      </button>
      <div class="nav-bar__center">
        <span class="nav-bar__step-label" v-if="!submitFinalError">Schritt {{ currentStep + 1 }}: {{ steps[currentStep] }}</span>
        <span class="nav-bar__step-label nav-bar__step-label--error" v-else>⚠ {{ submitFinalError }}</span>
      </div>
      <button
        v-if="currentStep < steps.length - 1"
        class="nav-btn nav-btn--primary"
        :disabled="currentStep === 0 || (currentStep === 1 && isProcessing)"
        @click="nextStep"
      >
        Weiter →
      </button>
      <button
        v-else
        class="nav-btn nav-btn--finish"
        :disabled="!completenessConfirmed || isSubmitting"
        @click="submitCase"
      >
        {{ isSubmitting ? 'Wird abgesendet…' : 'Antrag absenden ✓' }}
      </button>
    </div>

    <!-- Selection popover -->
    <SelectionPopover />

    <!-- Floating chat -->
    <ChatWindow form-id="form_3" :client-id="resolvedClientId" :ui-payload="currentUiPayload" />

    <!-- Finish modal -->
    <div v-if="showFinish" class="modal-overlay" @click.self="showFinish = false">
      <div class="modal">
        <h2>Antrag eingereicht!</h2>
        <p>
          Ihr Antrag {{ caseId }} wurde eingereicht und steht nun der Sachbearbeitung zur
          Prüfung zur Verfügung. Vielen Dank für das Erleben von FormIQ 3 Kategorisierung!
        </p>
        <div class="modal__actions">
          <button class="nav-btn nav-btn--secondary" @click="showFinish = false">Schließen</button>
          <button class="nav-btn nav-btn--primary" @click="goBack">← Zurück zum Dashboard</button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { ref, computed, onMounted, onUnmounted } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import ShowcaseBanner from '../components/ShowcaseBanner.vue';
import ChatWindow from '../components/ChatWindow.vue';
import SelectionPopover from '../components/SelectionPopover.vue';
import CaseDocumentCard from '../components/CaseDocumentCard.vue';
import CategorizedUploadDropzone from '../components/CategorizedUploadDropzone.vue';
import BaumAntragsdetailsStep, { sectionTitle, hasReviewableFields, fieldsFromExtraction } from '../forms/baumschutz/BaumAntragsdetailsStep.vue';
import BaumAngabenStep from '../forms/baumschutz/BaumAngabenStep.vue';
import BaumRechtlicheHinweiseStep from '../forms/baumschutz/BaumRechtlicheHinweiseStep.vue';
import { flowAssignmentApi } from '../services/flowAssignmentApi.js';
import { categoriesApi } from '../services/categoriesApi.js';
import { caseApi } from '../services/caseApi.js';
import { DOCUMENT_CATEGORIES } from '../constants/documentCategories.js';

const FLOW_KEY = 'formiq3';
const DEFAULT_CLIENT_ID = 'form3_baumschutz';

export default {
  name: 'ApplicationViewC',
  components: {
    ShowcaseBanner, ChatWindow, SelectionPopover, CaseDocumentCard, CategorizedUploadDropzone,
    BaumAntragsdetailsStep, BaumAngabenStep, BaumRechtlicheHinweiseStep,
  },

  setup() {
    const route = useRoute();
    const router = useRouter();
    const resolvedClientId = ref(DEFAULT_CLIENT_ID);
    // Category options: id = the raw document_type string SemanticBridge's
    // workflow config expects (e.g. "VOLLMACHT"), label = display text from
    // the local DOCUMENT_CATEGORIES lookup, sourced from the DOCUMENT_CATEGORISE
    // workflow's own meta_data.document_types (see categoriesApi.js).
    const categories = ref([]);

    const caseId = ref(route.params.caseId || null);
    const currentStep = ref(caseId.value ? 1 : 0);

    const documents = ref([]);
    const loadError = ref(null);
    const caseStatus = ref(null);
    const isProcessing = computed(() => caseStatus.value === 'IN_PROGRESS');
    const isExtractionFailed = computed(() => caseStatus.value === 'FAILED');
    const POLL_INTERVAL_MS = 4000;
    let pollTimer = null;

    const isCreating = ref(false);
    const createError = ref(null);
    const isAdding = ref(false);
    const addError = ref(null);
    // Covers the create-case call and the async extraction job as one
    // continuous overlay, so the citizen never sees the step 1 document list
    // mid-extraction (isCreating stays true until the first loadCase() -
    // which already reflects IN_PROGRESS - resolves, so this never flickers).
    const showLoadingMask = computed(() => isCreating.value || isProcessing.value);
    // Step 1 (document review) starts read-only - category was already fixed
    // at upload, so there's nothing to correct here. Toggled on only when the
    // citizen wants to add or remove documents.
    const isEditMode = ref(false);
    // Set whenever the citizen adds/removes a document on step 2 - neither
    // action retriggers extraction itself, so this drives the "Neu auswerten"
    // banner/button until they explicitly restart it (see reextract()).
    const documentsDirty = ref(false);
    const isReextracting = ref(false);
    const reextractError = ref(null);
    const completenessConfirmed = ref(false);
    const showFinish = ref(false);
    const isSubmitting = ref(false);
    const submitFinalError = ref(null);
    const showErrors = ref(false);

    // Document extractions delivered by FormIQ 3 (case.data.document_extractions),
    // grouped per document type - editable copy backing BaumAntragsdetailsStep.
    // Each entry gets its own wizard step (see antragsdetailsStart/angabenStepIndex
    // below), so the stepper size grows with however many categories were detected.
    const extractions = ref([]);

    const antragsdetailsStart = 2;

    // Categories with nothing left to review after stripping metadata (e.g.
    // MISC, which only ever carries predicted_document_type/certainty) don't
    // get their own Antragsdetails step - there'd be nothing to show but the
    // "keine weiteren Angaben" empty-hint. Keeps the originalIndex around so
    // edits still write back to the right slot in the unfiltered extractions
    // array (which is what actually gets submitted - see submitCase).
    const reviewableExtractions = computed(() => extractions.value
      .map((item, originalIndex) => ({ item, originalIndex }))
      .filter(({ item }) => hasReviewableFields(item.extraction)));

    // At least one category step even before extractions have loaded, so the
    // stepper never collapses to zero "Antragsdetails" steps. Once loaded,
    // the count follows the reviewable extractions only (may be 0 if every
    // uploaded document turned out to be MISC/unidentified).
    const antragsdetailsStepCount = computed(() => (
      extractions.value.length ? reviewableExtractions.value.length : 1
    ));
    const angabenStepIndex = computed(() => antragsdetailsStart + antragsdetailsStepCount.value);
    const rechtlicheStepIndex = computed(() => angabenStepIndex.value + 1);

    const steps = computed(() => {
      const categorySteps = extractions.value.length
        ? reviewableExtractions.value.map(({ item }) => sectionTitle(item.document_type))
        : ['Antragsdetails prüfen'];
      return [
        'Dokumente hochladen',
        'Kategorien prüfen',
        ...categorySteps,
        'Angaben prüfen',
        'Rechtliche Hinweise',
        'Zusammenfassung & Absenden',
      ];
    });

    // Keys for the local sidebar/progress payload only - kept unique per step
    // so `form_progress.steps` below never collides. The FormIQ 3 chat context
    // lookup itself always uses the single shared 'baum-antragsdetails-pruefen'
    // screen_id for every category step (see currentUiPayload) since the
    // backend only has one screen configuration document covering all
    // categories, and that content already speaks to "je Kategorie".
    const screenIds = computed(() => {
      const categoryIds = extractions.value.length
        ? reviewableExtractions.value.map((_, idx) => `baum-antragsdetails-pruefen-${idx}`)
        : ['baum-antragsdetails-pruefen'];
      return [
        'baum-dokumente-hochladen',
        'baum-kategorien-pruefen',
        ...categoryIds,
        'baum-angaben-pruefen',
        'baum-rechtliche-hinweise',
        'baum-zusammenfassung',
      ];
    });

    const formData = ref({
      vorname: '',
      nachname: '',
      geburtsdatum: '',
      geburtsort: '',
      email: '',
      mobil: '',
      confirm_rechtliche_hinweise: false,
    });

    const progressPercent = computed(() => Math.round((currentStep.value / (steps.value.length - 1)) * 100));

    const formatDate = (val) => {
      if (!val) return null;
      const d = new Date(val);
      if (isNaN(d)) return val;
      return d.toLocaleDateString('de-DE');
    };

    const formatExtractionValue = (value) => {
      if (Array.isArray(value)) return value.length ? `${value.length} Baum/Bäume erfasst` : null;
      return value || null;
    };

    // One card per detected document category, listing the same fields/labels
    // BaumAntragsdetailsStep showed during review - every non-empty value came
    // from FormIQ 3's document extraction, so it's flagged AutoFill here too.
    const summaryCategories = computed(() => reviewableExtractions.value.map(({ item }) => ({
      title: sectionTitle(item.document_type),
      fields: fieldsFromExtraction(item.extraction).map((field) => {
        const value = formatExtractionValue(item.extraction?.[field.key]);
        return { label: field.label, value, autofilled: !!value };
      }),
    })));

    // Angaben/Rechtliche Hinweise are manual entry (never extracted from
    // documents - see BaumAngabenStep's subtitle), so no AutoFill badge here.
    const angabenSummary = computed(() => {
      const d = formData.value;
      return [
        { label: 'Vorname', value: d.vorname },
        { label: 'Nachname', value: d.nachname },
        { label: 'Geburtsdatum', value: formatDate(d.geburtsdatum) },
        { label: 'Geburtsort', value: d.geburtsort },
        { label: 'E-Mail', value: d.email },
        { label: 'Mobiltelefon', value: d.mobil },
      ];
    });

    // Documents are grouped by category - the citizen assigns the real
    // category per file at upload time now (document_type itself), with
    // category_user/category_model as later corrections taking precedence.
    const groupedDocuments = computed(() => {
      const pending = [];
      const buckets = new Map();
      categories.value.forEach((c) => buckets.set(c.id, { id: c.id, label: c.label, docs: [] }));
      const fallbackId = categories.value.find((c) => c.id.toLowerCase() === 'misc')?.id || null;

      documents.value.forEach((doc) => {
        const value = (doc.category_user || doc.category_model || doc.document_type || '').trim();
        if (!value) {
          pending.push(doc);
          return;
        }
        const match = categories.value.find(
          (c) => c.id.toLowerCase() === value.toLowerCase() || c.label.toLowerCase() === value.toLowerCase()
        );
        const key = match ? match.id : fallbackId;
        if (key && buckets.has(key)) {
          buckets.get(key).docs.push(doc);
        } else if (buckets.has(value)) {
          buckets.get(value).docs.push(doc);
        } else {
          buckets.set(value, { id: value, label: value, docs: [doc] });
        }
      });

      const groups = [];
      if (pending.length) groups.push({ id: '__pending__', label: 'Wird noch kategorisiert', docs: pending });
      buckets.forEach((group) => { if (group.docs.length) groups.push(group); });
      return groups;
    });

    // Polls while extraction is still running (IN_PROGRESS) so a citizen who
    // bookmarks/reloads this page sees the outcome (EXTRACTED/COMPLETE or a
    // clear FAILED notice) without having to manually refresh - see the
    // processing-banner / isExtractionFailed template blocks below.
    const stopPolling = () => {
      if (pollTimer) {
        clearTimeout(pollTimer);
        pollTimer = null;
      }
    };

    const loadCase = async () => {
      if (!caseId.value) return;
      stopPolling();
      loadError.value = null;
      try {
        const result = await caseApi.getCase(resolvedClientId.value, caseId.value);
        caseStatus.value = result.status || null;
        documents.value = result.documents || [];
        extractions.value = result.data?.document_extractions || [];
        // Restore previously-submitted Angaben/Rechtliche Hinweise so
        // revisiting a bookmarked case (or a page refresh) doesn't wipe them.
        if (result.data?.applicant) {
          formData.value = { ...formData.value, ...result.data.applicant };
        }
      } catch (e) {
        loadError.value = e.message || 'Antrag konnte nicht geladen werden.';
      }
      if (caseStatus.value === 'IN_PROGRESS') {
        pollTimer = setTimeout(loadCase, POLL_INTERVAL_MS);
      }
    };

    onMounted(async () => {
      try {
        const assignment = await flowAssignmentApi.getFlowAssignment(FLOW_KEY);
        if (assignment?.client_id) resolvedClientId.value = assignment.client_id;
      } catch {
        // keep the default client_id if the lookup fails
      }
      try {
        const documentTypes = await categoriesApi.getDocumentTypes(resolvedClientId.value);
        categories.value = documentTypes.map((type) => {
          const match = DOCUMENT_CATEGORIES.find((c) => c.id.toLowerCase() === type.toLowerCase());
          return { id: type, label: match?.label || type, description: match?.description || '' };
        });
      } catch {
        // fall back to the hardcoded DOCUMENT_CATEGORIES list (ids won't
        // match real document_type values, but the UI still renders)
        categories.value = DOCUMENT_CATEGORIES;
      }
      await loadCase();
    });

    onUnmounted(() => stopPolling());

    // Uploads require one document per configured category in a single batch
    // (SemanticBridge's DocumentProcessor rejects a job missing any of them),
    // so the citizen assigns a real category to every file before submitting.
    const createCase = async (filesByType) => {
      isCreating.value = true;
      createError.value = null;
      try {
        const newCaseId = await caseApi.uploadCase(resolvedClientId.value, filesByType, { categorise: true });
        caseId.value = newCaseId;
        router.replace(`/application/tree-protection/${newCaseId}`);
        currentStep.value = 1;
        await loadCase();
      } catch (e) {
        createError.value = e.message || 'Hochladen fehlgeschlagen.';
      } finally {
        isCreating.value = false;
      }
    };

    const onDocumentDeleted = (documentId) => {
      documents.value = documents.value.filter((d) => d.document_id !== documentId);
      documentsDirty.value = true;
    };

    const updateExtractionItem = (index, item) => {
      extractions.value = extractions.value.map((existing, i) => (i === index ? item : existing));
    };

    // Adds documents to an already-created case one at a time (FIQ3-B01's
    // add_document endpoint) - this does not trigger a new extraction job,
    // it only attaches more documents to the case's document list.
    const onAddFiles = async (filesByType) => {
      isAdding.value = true;
      addError.value = null;
      try {
        for (const [documentType, files] of Object.entries(filesByType)) {
          for (const file of files) {
            // eslint-disable-next-line no-await-in-loop
            const result = await caseApi.addDocument(resolvedClientId.value, caseId.value, documentType, file);
            documents.value = result.documents || documents.value;
            documentsDirty.value = true;
          }
        }
      } catch (e) {
        addError.value = e.message || 'Hochladen fehlgeschlagen.';
      } finally {
        isAdding.value = false;
      }
    };

    const reextract = async () => {
      isReextracting.value = true;
      reextractError.value = null;
      try {
        await caseApi.reextractCase(resolvedClientId.value, caseId.value);
        documentsDirty.value = false;
        await loadCase();
      } catch (e) {
        reextractError.value = e.message || 'Neuauswertung konnte nicht gestartet werden.';
      } finally {
        isReextracting.value = false;
      }
    };

    const goToStep = (index) => {
      if (index > currentStep.value) return;
      // Step 0 is the "create case" upload screen; once a case exists there's
      // nothing to create anymore, so route back-navigation to it into step 1
      // (view/add documents) instead of re-triggering case creation.
      currentStep.value = caseId.value && index === 0 ? 1 : index;
      showErrors.value = false;
    };

    const validateCurrentStep = () => {
      const d = formData.value;
      if (currentStep.value === 1) return documents.value.length > 0 && !isProcessing.value;
      if (currentStep.value === angabenStepIndex.value) {
        return !!(d.vorname && d.nachname && d.geburtsdatum && d.geburtsort && d.email && d.mobil);
      }
      if (currentStep.value === rechtlicheStepIndex.value) return !!d.confirm_rechtliche_hinweise;
      return true;
    };

    const nextStep = () => {
      if (!validateCurrentStep()) {
        showErrors.value = true;
        return;
      }
      showErrors.value = false;
      if (currentStep.value < steps.value.length - 1) currentStep.value++;
    };

    const prevStep = () => {
      showErrors.value = false;
      if (currentStep.value > 0) currentStep.value--;
    };

    const submitCase = async () => {
      isSubmitting.value = true;
      submitFinalError.value = null;
      try {
        await caseApi.submitCase(resolvedClientId.value, caseId.value, extractions.value, formData.value);
        showFinish.value = true;
      } catch (e) {
        submitFinalError.value = e.message || 'Antrag konnte nicht abgesendet werden.';
      } finally {
        isSubmitting.value = false;
      }
    };

    const goBack = () => router.push('/dashboard');

    // Field lists are per logical step, not per index, since the number of
    // Antragsdetails category steps varies with how many document types were
    // detected - those steps carry no formData fields of their own.
    const currentUiPayload = computed(() => {
      const step = currentStep.value;
      const d = formData.value;

      let fields = [];
      let required = [];
      if (step === angabenStepIndex.value) {
        fields = required = ['vorname', 'nachname', 'geburtsdatum', 'geburtsort', 'email', 'mobil'];
      } else if (step === rechtlicheStepIndex.value) {
        fields = required = ['confirm_rechtliche_hinweise'];
      }

      // Category steps all share the one 'baum-antragsdetails-pruefen' screen
      // configuration on the backend (see screenIds above for why).
      const sid = step >= antragsdetailsStart && step < angabenStepIndex.value
        ? 'baum-antragsdetails-pruefen'
        : screenIds.value[step];

      const field_values = { document_count: documents.value.length };
      fields.forEach((k) => { field_values[k] = d[k]; });

      const field_errors = {};
      if (showErrors.value) {
        required.forEach((k) => {
          const v = d[k];
          if (v === '' || v === null || v === false || v === undefined) {
            field_errors[k] = 'Dieses Feld ist erforderlich.';
          }
        });
      }

      const form_progress = { current_step: step + 1, total_steps: steps.value.length, steps: {} };
      steps.value.forEach((name, i) => {
        const status = i < step ? 'completed' : i === step ? 'in_progress' : 'pending';
        form_progress.steps[screenIds.value[i]] = { name, status };
      });

      return { screen_id: sid, field_values, field_errors, form_progress };
    });

    return {
      steps, currentStep, progressPercent,
      antragsdetailsStart, angabenStepIndex, rechtlicheStepIndex,
      resolvedClientId, categories, groupedDocuments,
      caseId, documents, loadError, formData, extractions, reviewableExtractions, showErrors,
      summaryCategories, angabenSummary,
      isProcessing, isExtractionFailed, showLoadingMask, isEditMode,
      isCreating, createError, isAdding, addError,
      documentsDirty, isReextracting, reextractError, reextract,
      completenessConfirmed, showFinish, isSubmitting, submitFinalError,
      createCase, onDocumentDeleted, onAddFiles, updateExtractionItem,
      goToStep, nextStep, prevStep, submitCase, goBack,
      currentUiPayload,
    };
  },
};
</script>

<style scoped src="../forms/form.css"></style>
<style scoped>
.app-view {
  height: 100vh;
  display: flex;
  flex-direction: column;
  background: #eef0f4;
  overflow: hidden;
}

/* Header */
.app-view__header {
  background: #ffffff;
  border-bottom: 3px solid #c0121c;
  padding: 10px 24px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  color: #0f172a;
  flex-shrink: 0;
  gap: 16px;
}

.back-btn {
  background: #f1f5f9;
  border: 1px solid #d0d7de;
  color: #1e293b;
  padding: 7px 14px;
  border-radius: 4px;
  font-size: 13px;
  font-weight: 600;
  cursor: pointer;
  white-space: nowrap;
  flex-shrink: 0;
}
.back-btn:hover { background: #e2e8f0; }

.header__title { display: flex; align-items: center; gap: 12px; flex: 1; }
.header__icon { font-size: 22px; }
.header__name { display: block; font-size: 15px; font-weight: 700; color: #1a1a1a; }
.header__sub { display: block; font-size: 11px; color: #64748b; margin-top: 2px; }

.header__badge {
  background: #f0fdf4;
  border: 1px solid #bbf7d0;
  color: #065f46;
  padding: 5px 12px;
  border-radius: 20px;
  font-size: 12px;
  font-weight: 700;
  flex-shrink: 0;
}

/* Body: form + sidebar */
.app-view__body {
  flex: 1;
  display: flex;
  flex-direction: row;
  overflow: hidden;
  min-height: 0;
}

/* Form area */
.form-area {
  flex: 1;
  overflow-y: auto;
  min-height: 0;
  background: #fff;
  border-right: 1px solid #d0d7de;
  padding: 24px;
  display: flex;
  flex-direction: column;
  gap: 20px;
}

.form-area__intro {
  font-size: 14px;
  color: #555;
  line-height: 1.6;
}

.bookmark-notice {
  background: #eef4fb;
  border: 1px solid #c3d9f0;
  border-radius: 10px;
  padding: 12px 16px;
  font-size: 13px;
  color: #1d4ed8;
}

.notice { font-size: 13px; padding: 8px 12px; border-radius: 8px; }
.notice--error { background: #fef2f2; color: #dc2626; }

.spinner {
  width: 16px;
  height: 16px;
  flex-shrink: 0;
  border: 2px solid #c3d9f0;
  border-top-color: #1d4ed8;
  border-radius: 50%;
  animation: spin 0.8s linear infinite;
}
.spinner--lg { width: 36px; height: 36px; border-width: 3px; }
@keyframes spin { to { transform: rotate(360deg); } }

.loading-mask {
  position: fixed;
  inset: 0;
  z-index: 3000;
  display: flex;
  align-items: center;
  justify-content: center;
  background: rgba(255, 255, 255, 0.92);
  backdrop-filter: blur(1px);
}
.loading-mask__content {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 14px;
  text-align: center;
  max-width: 360px;
  padding: 32px;
}
.loading-mask__title { font-size: 15px; font-weight: 700; color: #003366; margin: 0; }
.loading-mask__hint { font-size: 13px; color: #6b7280; margin: 0; }

.reextract-banner {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 14px;
  background: #fffbeb;
  border: 1px solid #fde68a;
  border-radius: 10px;
  padding: 12px 16px;
}
.reextract-banner p { font-size: 13px; color: #92400e; line-height: 1.5; margin: 0; }
.reextract-banner .nav-btn { padding: 8px 18px; font-size: 13px; flex-shrink: 0; }

.section-title { font-size: 14px; font-weight: 700; color: #1a1a2e; margin-bottom: 10px; }
.empty-hint { font-size: 13px; color: #9ca3af; }

.documents-section, .add-more-section { display: flex; flex-direction: column; gap: 10px; }

.documents-section__header {
  display: flex;
  align-items: center;
  justify-content: space-between;
}
.documents-section__header .section-title { margin-bottom: 0; }

.edit-toggle-btn {
  background: #eef4fb;
  border: 1px solid #c3d9f0;
  color: #1e6eb5;
  padding: 5px 14px;
  border-radius: 20px;
  font-size: 12px;
  font-weight: 700;
  cursor: pointer;
}
.edit-toggle-btn:hover { background: #dbe9f7; }

/* Grid instead of a single-column stack - full-width cards left most of a
   card's row empty once the category/picker content was trimmed down. */
.category-group {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(220px, 1fr));
  gap: 8px;
  margin-bottom: 10px;
}
.category-group__header {
  grid-column: 1 / -1;
  display: flex;
  align-items: center;
  gap: 8px;
}
.category-group__label {
  font-size: 12px;
  font-weight: 700;
  color: #003366;
  text-transform: uppercase;
  letter-spacing: 0.04em;
}
.category-group__count {
  font-size: 11px;
  font-weight: 700;
  background: #eef4fb;
  color: #1d4ed8;
  border-radius: 10px;
  padding: 1px 8px;
}

.completeness {
  background: #f9fafb;
  border-radius: 10px;
  padding: 14px 16px;
}
.completeness__checkbox {
  display: flex;
  align-items: center;
  gap: 10px;
  font-size: 13px;
  color: #374151;
  cursor: pointer;
}

/* Sidebar */
.sidebar {
  width: 220px;
  flex-shrink: 0;
  background: #f5f7fa;
  border-left: 1px solid #d0d7de;
  display: flex;
  flex-direction: column;
  overflow-y: auto;
}

.sidebar__header {
  padding: 16px 16px 12px;
  border-bottom: 2px solid #c0121c;
  display: flex;
  align-items: center;
  gap: 8px;
}

.sidebar__icon { font-size: 16px; }

.sidebar__title {
  font-size: 12px;
  font-weight: 700;
  color: #c0121c;
  text-transform: uppercase;
  letter-spacing: 0.06em;
}

.sidebar__nav {
  flex: 1;
  padding: 8px 0;
}

.sidebar__item {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 10px 16px;
  cursor: default;
  border-left: 3px solid transparent;
  transition: background 0.15s, border-color 0.15s;
}

.sidebar__item--done {
  cursor: pointer;
  border-left-color: #1a7a45;
}
.sidebar__item--done:hover { background: #e8f5ee; }

.sidebar__item--active {
  background: #eef4fb;
  border-left-color: #003366;
}

.sidebar__item--locked { opacity: 0.55; }

.sidebar__indicator {
  width: 24px;
  height: 24px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 11px;
  font-weight: 700;
  flex-shrink: 0;
}

.indicator--done {
  background: #1a7a45;
  color: #fff;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 24px;
  height: 24px;
  border-radius: 50%;
  font-size: 11px;
  font-weight: 700;
}

.indicator--active {
  background: #003366;
  color: #fff;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 24px;
  height: 24px;
  border-radius: 50%;
  font-size: 11px;
  font-weight: 700;
  box-shadow: 0 0 0 3px rgba(0, 51, 102, 0.18);
}

.indicator--pending {
  background: #e2e8f0;
  color: #6b7280;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 24px;
  height: 24px;
  border-radius: 50%;
  font-size: 11px;
  font-weight: 700;
}

.sidebar__label {
  font-size: 13px;
  color: #1a1a1a;
  font-weight: 500;
  line-height: 1.3;
}

.sidebar__item--active .sidebar__label {
  font-weight: 700;
  color: #003366;
}

.sidebar__item--done .sidebar__label {
  color: #155534;
}

.sidebar__progress {
  padding: 14px 16px;
  border-top: 1px solid #d0d7de;
}

.progress-bar {
  height: 5px;
  background: #d0d7de;
  border-radius: 3px;
  overflow: hidden;
  margin-bottom: 6px;
}

.progress-bar__fill {
  height: 100%;
  background: #003366;
  border-radius: 3px;
  transition: width 0.35s ease;
}

.progress-text {
  font-size: 11px;
  color: #6b7280;
  font-weight: 500;
}

/* Navigation bar */
.nav-bar {
  background: #f5f7fa;
  border-top: 2px solid #d0d7de;
  padding: 12px 24px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  flex-shrink: 0;
  gap: 12px;
}

.nav-bar__center {
  flex: 1;
  text-align: center;
}

.nav-bar__step-label {
  font-size: 12px;
  color: #6b7280;
  font-weight: 500;
}
.nav-bar__step-label--error { color: #dc2626; font-weight: 700; }

.nav-btn {
  padding: 10px 28px;
  border-radius: 3px;
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
  border: none;
  transition: opacity 0.15s, transform 0.1s, background 0.15s;
}
.nav-btn:hover:not(:disabled) { opacity: 0.88; transform: translateY(-1px); }
.nav-btn:disabled { opacity: 0.35; cursor: not-allowed; }
.nav-btn--primary { background: #003366; color: #fff; }
.nav-btn--primary:hover:not(:disabled) { background: #002244; opacity: 1; }
.nav-btn--secondary { background: #ffffff; color: #1a1a1a; border: 1px solid #adb5bd; }
.nav-btn--secondary:hover:not(:disabled) { background: #f0f4f9; opacity: 1; }
.nav-btn--finish { background: #1a7a45; color: #fff; }
.nav-btn--finish:hover:not(:disabled) { background: #155534; opacity: 1; }

/* Modal */
.modal-overlay {
  position: fixed;
  inset: 0;
  background: rgba(0,0,0,0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 2000;
}
.modal {
  background: #fff;
  border-radius: 8px;
  padding: 40px 36px;
  max-width: 440px;
  width: 90%;
  text-align: center;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 16px;
  box-shadow: 0 20px 60px rgba(0,0,0,0.2);
  border-top: 4px solid #c0121c;
}
.modal h2 { font-size: 22px; font-weight: 800; color: #1a1a2e; }
.modal p { font-size: 14px; color: #555; line-height: 1.6; }
.modal__actions { display: flex; gap: 12px; justify-content: center; flex-wrap: wrap; }

/* Responsive */
@media (max-width: 700px) {
  .sidebar { display: none; }
}
</style>
